<template>
  <Modal
    :modal-active="modelValue"
    :has-header-border="true"
    :has-footer-border="true"
    max-width="max-w-6xl"
    max-height="90vh"
    @close="close"
  >
    <template #header>
      <!-- Vocabulário Q1/Q13: o que o emissor manda é ANEXO, nunca "evidência".
           O `EvidenceModal` da folha mantém o título antigo de propósito. -->
      <h2 class="text-left text-lg text-textPrimary" data-testid="activity-attachments-title">
        Anexos do Registro de Atividade
      </h2>
    </template>

    <div class="flex h-full flex-col gap-4 lg:flex-row">
      <div class="flex min-w-0 flex-1 flex-col gap-3">
        <div class="flex items-center gap-2">
          <span
            v-if="containerId"
            class="rounded-lg bg-gray-200 px-2 py-1 text-xl text-textPrimary"
            data-testid="activity-attachments-container-id"
            >#{{ containerId }}</span
          >
          <span class="truncate text-xl font-semibold text-textPrimary">{{
            currentAttachment?.originalFileName ?? 'Sem anexo'
          }}</span>
        </div>

        <div
          v-if="loadError"
          class="flex flex-col items-start gap-2 rounded border border-negative bg-red-50 p-3 text-left text-sm text-negative"
          data-testid="activity-attachments-error"
          role="alert"
        >
          <span>{{ loadError }}</span>
          <button
            type="button"
            class="font-semibold underline"
            data-testid="activity-attachments-retry"
            @click="loadCurrent"
          >
            Tentar novamente
          </button>
        </div>

        <div
          v-else-if="isLoading"
          class="py-10 text-center text-sm text-gray-500"
          data-testid="activity-attachments-loading"
        >
          Carregando anexo…
        </div>

        <StepPreview
          v-else-if="currentUrl"
          :file="null"
          :file-url="currentUrl"
          :file-type="currentType"
          :rotation="rotation"
          :zoom="zoom"
          mode="preview"
          :magnifier="magnifier"
          magnifier-toggle
          :show-display-name="false"
          @update:rotation="rotation = $event"
          @update:zoom="zoom = $event"
          @update:magnifier="magnifier = $event"
        />
      </div>

      <div
        v-if="attachments.length > 1"
        class="flex w-full shrink-0 flex-col gap-1 text-left lg:w-[260px]"
      >
        <span class="text-base font-bold leading-6 text-textPrimary"
          >Anexos ({{ attachments.length }})</span
        >
        <!--
          Cada anexo é um `<button>`, não um `<li @click>`: a lista precisa ser alcançável
          por teclado e anunciada como acionável, e o anexo em exibição precisa se identificar
          por algo além da cor de fundo.
        -->
        <ul class="flex flex-col gap-1">
          <li v-for="(attachment, index) in attachments" :key="attachment.evidenceId">
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors"
              :class="
                index === selectedIndex
                  ? 'bg-primary30 font-semibold text-white'
                  : 'text-textPrimary hover:bg-gray-100'
              "
              data-testid="activity-attachment-item"
              :aria-current="index === selectedIndex ? 'true' : undefined"
              @click="selectAttachment(index)"
            >
              <span class="truncate">{{ attachment.originalFileName }}</span>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <OutlineButton
          data-testid="activity-attachments-close"
          text="Fechar"
          width="100px"
          height="32px"
          @click="close"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import Modal from '@/components/designSystem/modals/Modal.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import type { DSAttendanceActivityAttachment } from '@/types/ds-attendance.types'

/**
 * Anexo de um Registro de Atividade, como a fila e o Detalhes do Plantão listam.
 * O contrato mora em `types/ds-attendance.types.ts` (a listagem devolve os anexos
 * dentro do item do container); aqui fica só o apelido que os consumidores já
 * importam deste arquivo.
 */
export type ActivityAttachment = DSAttendanceActivityAttachment

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    containerId: number | null
    attachments: ActivityAttachment[]
  }>(),
  { attachments: () => [] }
)

const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const selectedIndex = ref(0)
const rotation = ref(0)
const zoom = ref(1)
const magnifier = ref(false)
const isLoading = ref(false)
const loadError = ref<string | null>(null)

// Cache por `evidenceId`: reabrir o mesmo anexo não baixa de novo, e a revogação
// no fechamento tem uma lista única para percorrer.
const objectUrls = ref(new Map<number, { url: string; type: string }>())

const currentAttachment = computed(() => props.attachments[selectedIndex.value] ?? null)
const currentEntry = computed(() =>
  currentAttachment.value ? objectUrls.value.get(currentAttachment.value.evidenceId) : undefined
)
const currentUrl = computed(() => currentEntry.value?.url ?? null)
const currentType = computed(() => currentEntry.value?.type)

/**
 * `blob:` fica preso à aba até ser revogado. O `EvidenceModal` já paga essa
 * disciplina; repetir aqui é obrigatório porque o anexo pode ter 50 MB.
 */
function revokeAll() {
  objectUrls.value.forEach(({ url }) => URL.revokeObjectURL(url))
  objectUrls.value = new Map()
}

onBeforeUnmount(revokeAll)

async function loadCurrent() {
  const attachment = currentAttachment.value
  const containerId = props.containerId
  if (!attachment || containerId == null) return
  if (objectUrls.value.has(attachment.evidenceId)) return

  isLoading.value = true
  loadError.value = null
  try {
    const blob = await Network.dsAttendance.getAttachmentFile(containerId, attachment.evidenceId)
    // `Map` novo (e não `.set` no mesmo): o computed precisa reagir à troca da
    // referência — mutação in-place de um `Map` não dispara render em Vue 3.
    const next = new Map(objectUrls.value)
    next.set(attachment.evidenceId, { url: URL.createObjectURL(blob), type: blob.type })
    objectUrls.value = next
  } catch (error) {
    console.error('Erro ao baixar o anexo do Registro de Atividade:', error)
    loadError.value = getApiErrorMessage(error, 'Não foi possível abrir o anexo.')
  } finally {
    isLoading.value = false
  }
}

function selectAttachment(index: number) {
  selectedIndex.value = index
  rotation.value = 0
  zoom.value = 1
  loadCurrent()
}

function close() {
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      selectedIndex.value = 0
      rotation.value = 0
      zoom.value = 1
      loadError.value = null
      loadCurrent()
      return
    }
    revokeAll()
  }
)
</script>
